/* Activity types: the categories everything else hangs off.
 *
 * A type is a name, a colour and one bit the planner reads — whether it counts
 * as work. That bit is the whole reason this panel matters: a type that does
 * not count as work may be planned into an evening or a weekend, and nothing
 * else in the app can make that true. Sharing is expressed per type as well
 * (see People), so a type is also the unit of "who can see this".
 *
 * Offered as suggestions on first use rather than written into the database:
 * somebody who wants a single type called "billable" should not have to delete
 * three first.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActivityType } from '@shared/types';
import { get, post, patch, del, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import Section from '../../ui/Section';
import Icon from '../../ui/Icon';
import { ConfirmDialog } from '../../ui/schedule';

interface TypesReply {
  types: ActivityType[];
  colors: string[];
  suggested: { name: string; color: string; countsAsWork?: boolean }[];
}

export default function Types() {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [color, setColor] = useState('blue');
  const [dropping, setDropping] = useState<ActivityType | null>(null);

  const types = useQuery({
    queryKey: ['activity-types', 'settings'],
    queryFn: async () => {
      const r = await get<TypesReply>('/api/activity-types');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your types.'));
      return r;
    }
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['activity-types'] });
    void qc.invalidateQueries({ queryKey: ['social'] });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
  };

  const add = useMutation({
    mutationFn: async (body: { name: string; color: string }) => {
      const r = await post('/api/activity-types', body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add that type.'));
      return body.name;
    },
    onSuccess: n => { setName(''); refresh(); toast.say(`${n} is a type now.`, 'good'); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const change = useMutation({
    mutationFn: async ({ t, body }: { t: ActivityType; body: Record<string, unknown> }) => {
      const r = await patch(`/api/activity-types/${t.id}`, body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return r;
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const remove = async (t: ActivityType) => {
    const r = await del(`/api/activity-types/${t.id}`);
    if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
    refresh();
    toast.say(`${t.name} is gone. Tasks filed under it are kept, without a type.`);
  };

  const list = (types.data?.types ?? []).filter(t => !t.archived);
  const colors = types.data?.colors ?? ['orange', 'amber', 'green', 'blue', 'violet', 'rose', 'slate'];
  const suggested = (types.data?.suggested ?? []).filter(s => !list.some(t => t.name === s.name));
  const notWork = list.filter(t => t.countsAsWork === false).length;

  return (
    <Section id="types-title" icon="tag" title="Activity types"
             meta={list.length
               ? `${list.length} types · ${notWork} can use evenings and weekends`
               : 'the categories your time is filed under'}
             info={<>A type is a colour on the calendar, a column in Tasks, and the unit of sharing
               on People. The switch that matters is <strong>Counts as work</strong>: switched off,
               the planner may put that type in an evening or at the weekend — which is how a
               haircut or a guitar lesson ever gets planned at all.</>}>

      {list.length > 0 && (
        <ul className="item-list">
          {list.map(t => (
            <li key={t.id} className="item type-row">
              <label className={`type-swatch swatch-${t.color}`} title="Change colour">
                <span className="sr-only">Colour for {t.name}</span>
                <select value={t.color}
                        onChange={e => change.mutate({ t, body: { color: e.target.value } })}>
                  {colors.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <div className="item-body">
                <input className="db-cell type-name" defaultValue={t.name} maxLength={40}
                       aria-label={`Name of ${t.name}`}
                       onBlur={e => {
                         const v = e.target.value.trim();
                         if (v && v !== t.name) change.mutate({ t, body: { name: v } });
                       }} />
                <span className="muted small">
                  {t.countsAsWork === false ? 'Any time you are awake' : 'Working hours only'}
                </span>
              </div>
              <div className="item-tools">
                <label className="switch" title="Counts as work">
                  <input type="checkbox" checked={t.countsAsWork !== false}
                         aria-label={`${t.name} counts as work`}
                         onChange={e => change.mutate({ t, body: { countsAsWork: e.target.checked } })} />
                  <span className="switch-track" aria-hidden="true"><span className="switch-thumb" /></span>
                  <span className="switch-label small"><Icon name="briefcase" size={14} /></span>
                </label>
                <button className="btn btn-ghost btn-icon btn-bad" onClick={() => setDropping(t)}
                        aria-label={`Remove ${t.name}`} title="Remove">
                  <Icon name="trash" size={17} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {suggested.length > 0 && (
        <div className="inline wrap" role="group" aria-label="Suggested types">
          <span className="muted small">One tap:</span>
          {suggested.map(s => (
            <button key={s.name} className="btn btn-ghost btn-sm"
                    disabled={add.isPending}
                    onClick={() => add.mutate({ name: s.name, color: s.color })}>
              <Icon name="plus" size={14} />{s.name}
            </button>
          ))}
        </div>
      )}

      <form className="inline wrap" onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (name.trim()) add.mutate({ name: name.trim(), color });
      }}>
        <label className={`type-swatch swatch-${color}`} title="Colour">
          <span className="sr-only">Colour for the new type</span>
          <select value={color} onChange={e => setColor(e.target.value)}>
            {colors.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <input value={name} maxLength={40} placeholder="New type — Client work, Errands…"
               aria-label="Name of a new type" onChange={e => setName(e.target.value)} />
        <button className="btn btn-primary btn-sm" type="submit" disabled={!name.trim() || add.isPending}>
          <Icon name="plus" size={16} />Add
        </button>
      </form>

      {dropping && (
        <ConfirmDialog
          title={`Remove ${dropping.name}?`}
          description="Tasks and blocks filed under it keep existing, without a type — and anything shared by this type stops being shared."
          confirmLabel="Remove it"
          danger
          onClose={() => setDropping(null)}
          onConfirm={() => remove(dropping)} />
      )}
    </Section>
  );
}
